import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Settings, 
  Mail, 
  Database, 
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  Send,
  Globe,
  Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate hour options
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function Admin() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_pass: '',
    email_from: '',
    mysql_host: '',
    mysql_port: '',
    mysql_database: '',
    mysql_user: '',
    mysql_pass: '',
    work_start_hour: '6',
    work_end_hour: '22',
    holiday_country: 'CH',
    default_portal_language: 'de',
    default_day_duration: 8.5,
    default_break_duration: 30,
    break_threshold_hours: 6
  });
  
  const [leaveBalances, setLeaveBalances] = useState([]);
  
  const [emailTestResult, setEmailTestResult] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/config`);
      setConfig(prev => ({ ...prev, ...res.data }));
      // Pre-fill test email with user's email
      if (user?.email) {
        setTestEmailAddress(user.email);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const fetchLeaveBalances = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats/leave-balances`);
      setLeaveBalances(res.data);
    } catch (err) {
      console.error('Error fetching leave balances:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchLeaveBalances();
  }, [fetchConfig, fetchLeaveBalances]);

  const handleSaveConfig = async (section) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, {
        section,
        ...config
      });
      toast.success(t('configSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error(t('enterTestEmail'));
      return;
    }
    
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/test-email`, { 
        test_email: testEmailAddress 
      });
      setEmailTestResult({ success: true, message: res.data.message });
      toast.success(t('emailTestSuccess'));
    } catch (err) {
      setEmailTestResult({ success: false, message: err.response?.data?.detail || t('error') });
      toast.error(t('emailTestFailed'));
    } finally {
      setTestingEmail(false);
    }
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl">{t('administration').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">{t('systemConfiguration')}</p>
        </div>
      </div>

      <Tabs defaultValue="leave" className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="leave" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {t('leaveBalance')}
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Brevo / SMTP
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t('workHours')}
          </TabsTrigger>
          <TabsTrigger value="database" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            MySQL
          </TabsTrigger>
        </TabsList>

        {/* Leave Balances Table */}
        <TabsContent value="leave" className="mt-6">
          <div className="border border-border rounded-lg p-6 bg-card space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{t('leaveBalance')}</h2>
                <p className="text-sm text-muted-foreground">{t('leaveBalanceDescription')}</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchLeaveBalances}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('refresh')}
              </Button>
            </div>

            <div className="border border-border rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('collaborator')}</th>
                    <th className="text-right">{t('annualLeave')}</th>
                    <th className="text-right">{t('usedLeave')}</th>
                    <th className="text-right">{t('pendingLeave')}</th>
                    <th className="text-right">{t('remainingLeave')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map((item) => (
                    <tr key={item.user_id}>
                      <td className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="font-medium">{item.user_name}</span>
                      </td>
                      <td className="text-right mono">{item.total_allowance}{t('h')}</td>
                      <td className="text-right mono text-amber-600">{item.used}{t('h')}</td>
                      <td className="text-right mono text-blue-600">{item.pending}{t('h')}</td>
                      <td className="text-right">
                        <Badge className={`${item.remaining >= 0 ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>
                          {item.remaining}{t('h')}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {leaveBalances.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted-foreground py-8">
                        {t('noDataAvailable')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Email/Brevo Configuration */}
        <TabsContent value="email" className="mt-6">
          <div className="border border-border rounded-lg p-6 bg-card space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{t('emailConfiguration')}</h2>
                <p className="text-sm text-muted-foreground">{t('brevoSmtpSettings')}</p>
              </div>
              {emailTestResult && (
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${emailTestResult.success ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                  {emailTestResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {emailTestResult.success ? t('connected') : t('failed')}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('smtpHost')}</Label>
                <Input
                  value={config.smtp_host}
                  onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                  placeholder="smtp-relay.brevo.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('smtpPort')}</Label>
                <Input
                  value={config.smtp_port}
                  onChange={(e) => setConfig({ ...config, smtp_port: e.target.value })}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('smtpUser')}</Label>
                <Input
                  value={config.smtp_user}
                  onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                  placeholder="your-smtp-user"
                  data-testid="smtp-user-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('smtpPassword')}</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.smtp_pass ? 'text' : 'password'}
                    value={config.smtp_pass}
                    onChange={(e) => setConfig({ ...config, smtp_pass: e.target.value })}
                    placeholder="••••••••"
                    data-testid="smtp-pass-input"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword('smtp_pass')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords.smtp_pass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t('emailFrom')}</Label>
                <Input
                  type="email"
                  value={config.email_from}
                  onChange={(e) => setConfig({ ...config, email_from: e.target.value })}
                  placeholder="noreply@vitalis-sports.ch"
                  data-testid="email-from-input"
                />
              </div>
            </div>

            {/* Test Email Section */}
            <div className="border-t border-border pt-4 space-y-4">
              <h3 className="text-lg font-medium">{t('testEmailConnection')}</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1 space-y-2">
                  <Label>{t('testEmailAddress')}</Label>
                  <Input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="test@example.com"
                    data-testid="test-email-input"
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={handleTestEmail}
                  disabled={testingEmail || !testEmailAddress}
                  data-testid="send-test-email-btn"
                >
                  {testingEmail ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {testingEmail ? t('sending') : t('sendTestEmail')}
                </Button>
              </div>
              {emailTestResult && (
                <p className={`text-sm ${emailTestResult.success ? 'text-emerald-600' : 'text-red-600'}`}>
                  {emailTestResult.message}
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={() => handleSaveConfig('email')}
                disabled={saving}
                data-testid="save-email-config-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Work Hours Configuration */}
        <TabsContent value="schedule" className="mt-6">
          <div className="border border-border rounded-lg p-6 bg-card space-y-6">
            <div>
              <h2 className="text-xl font-semibold">{t('workHoursConfiguration')}</h2>
              <p className="text-sm text-muted-foreground">{t('workHoursDescription')}</p>
            </div>

            {/* Portal Language */}
            <div className="border-b border-border pb-6">
              <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5" />
                {t('portalLanguage')}
              </h3>
              <div className="max-w-xs space-y-2">
                <Label>{t('defaultLanguage')}</Label>
                <Select
                  value={config.default_portal_language || 'de'}
                  onValueChange={(v) => setConfig({ ...config, default_portal_language: v })}
                >
                  <SelectTrigger data-testid="default-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                    <SelectItem value="fr">🇫🇷 Français</SelectItem>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Work Hours */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
              <div className="space-y-2">
                <Label>{t('dayStartHour')}</Label>
                <Select
                  value={config.work_start_hour}
                  onValueChange={(v) => setConfig({ ...config, work_start_hour: v })}
                >
                  <SelectTrigger data-testid="work-start-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h} value={h.toString()}>
                        {h.toString().padStart(2, '0')}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('dayEndHour')}</Label>
                <Select
                  value={config.work_end_hour}
                  onValueChange={(v) => setConfig({ ...config, work_end_hour: v })}
                >
                  <SelectTrigger data-testid="work-end-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h} value={h.toString()}>
                        {h.toString().padStart(2, '0')}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Default Day Duration & Break Settings */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
                <Clock className="h-5 w-5" />
                {t('defaultDuration')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
                <div className="space-y-2">
                  <Label>{t('defaultDayDuration')}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="1"
                    max="12"
                    value={config.default_day_duration || 8.5}
                    onChange={(e) => setConfig({ ...config, default_day_duration: parseFloat(e.target.value) || 8.5 })}
                    data-testid="default-day-duration"
                  />
                  <p className="text-xs text-muted-foreground">{t('hours')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('defaultBreakDuration')}</Label>
                  <Input
                    type="number"
                    step="5"
                    min="0"
                    max="120"
                    value={config.default_break_duration || 30}
                    onChange={(e) => setConfig({ ...config, default_break_duration: parseInt(e.target.value) || 30 })}
                    data-testid="default-break-duration"
                  />
                  <p className="text-xs text-muted-foreground">{t('minutes')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('breakThreshold')}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="1"
                    max="12"
                    value={config.break_threshold_hours || 6}
                    onChange={(e) => setConfig({ ...config, break_threshold_hours: parseFloat(e.target.value) || 6 })}
                    data-testid="break-threshold-hours"
                  />
                  <p className="text-xs text-muted-foreground">{t('breakThresholdDesc')}</p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                <strong>{t('note')}:</strong> {t('workHoursNote')}
              </p>
            </div>

            {/* Holiday Country Selection */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
                <Globe className="h-5 w-5" />
                {t('holidayCountry')}
              </h3>
              <div className="max-w-xs space-y-2">
                <Label>{t('selectCountry')}</Label>
                <Select
                  value={config.holiday_country || 'CH'}
                  onValueChange={(v) => setConfig({ ...config, holiday_country: v })}
                >
                  <SelectTrigger data-testid="holiday-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CH">
                      <span className="flex items-center gap-2">
                        🇨🇭 {t('switzerland')}
                      </span>
                    </SelectItem>
                    <SelectItem value="FR">
                      <span className="flex items-center gap-2">
                        🇫🇷 {t('france')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>{t('note')}:</strong> {t('holidayCountryNote')}
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={() => handleSaveConfig('schedule')}
                disabled={saving}
                data-testid="save-schedule-config-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* MySQL Configuration */}
        <TabsContent value="database" className="mt-6">
          <div className="border border-border rounded-lg p-6 bg-card space-y-6">
            <div>
              <h2 className="text-xl font-semibold">{t('databaseConfiguration')}</h2>
              <p className="text-sm text-muted-foreground">{t('mysqlSettings')}</p>
            </div>

            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>{t('note')}:</strong> {t('mysqlMigrationNote')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('mysqlHost')}</Label>
                <Input
                  value={config.mysql_host}
                  onChange={(e) => setConfig({ ...config, mysql_host: e.target.value })}
                  placeholder="mysql.example.com"
                  data-testid="mysql-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('mysqlPort')}</Label>
                <Input
                  value={config.mysql_port}
                  onChange={(e) => setConfig({ ...config, mysql_port: e.target.value })}
                  placeholder="3306"
                  data-testid="mysql-port-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('mysqlDatabase')}</Label>
                <Input
                  value={config.mysql_database}
                  onChange={(e) => setConfig({ ...config, mysql_database: e.target.value })}
                  placeholder="vitalis_sports"
                  data-testid="mysql-database-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('mysqlUser')}</Label>
                <Input
                  value={config.mysql_user}
                  onChange={(e) => setConfig({ ...config, mysql_user: e.target.value })}
                  placeholder="db_user"
                  data-testid="mysql-user-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t('mysqlPassword')}</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.mysql_pass ? 'text' : 'password'}
                    value={config.mysql_pass}
                    onChange={(e) => setConfig({ ...config, mysql_pass: e.target.value })}
                    placeholder="••••••••"
                    data-testid="mysql-pass-input"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword('mysql_pass')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords.mysql_pass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={() => handleSaveConfig('database')}
                disabled={saving}
                data-testid="save-mysql-config-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
